'use client';

import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
  useMemo,
} from 'react';
import { useRouter } from 'next/navigation';
import { useFormatLink } from 'components/shared/CustomLink';

export type Variant = 'Phantom' | 'Cryo';

export interface AccordionItem {
  title: string;
  subtitle: string;
  image: string;
}

export interface ResponsiveImagePath {
  web: string;
  mobile: string;
}

export interface VariantData {
  leftImagePath: ResponsiveImagePath;
  rightImagePath: ResponsiveImagePath;
  slantedText: string;
  modelImagePath: string;
  accordionItems: AccordionItem[];
  galleryImages: string[];
}

export const VARIANT_DATA: Record<Variant, VariantData> = {
  Phantom: {
    leftImagePath: {
      web: '/web_v2/uh-x-diesel/variants/phantom/phantom-lifestyle-gradient-new.png',
      mobile:
        '/web_v2/uh-x-diesel/variants/phantom/phantom-lifestyle-banner.png',
    },
    rightImagePath: {
      web: '/web_v2/uh-x-diesel/variants/phantom/phantom-ring-gradient.png',
      mobile: '/web_v2/uh-x-diesel/variants/phantom/phantom-render-v1.png',
    },
    slantedText:
      'uniting Diesel’s iconic Double-D design with                          Ultrahuman’s advanced technology, the Phantom variant, finished in a dark Distressed tone,           embodies an elusivE and gritty presence.                           Industrial  at  heart, refined in form.',
    modelImagePath: '/web_v2/uh-x-diesel/specs/phantom-model.png',
    accordionItems: [
      {
        title: 'Durability',
        subtitle:
          'Built to last with an outer shell made from Space-Grade Titanium. With up to 100 m/328 ft water resistance, you can explore the uncharted, uninterrupted.',
        image: '/web_v2/uh-x-diesel/specs/final/phantom-durability.png',
      },
      {
        title: 'Tech & Build',
        subtitle:
          'Designed for comfort with a seamless, hypoallergenic resin. The inner shell houses a 6-Axis Motion Sensor for movement, a PPG Sensor for Heart Rate & Blood Oxygen, and a medical-grade temperature sensor.',
        image: '/web_v2/uh-x-diesel/specs/final/phantom-tech.png',
      },
      {
        title: 'Battery & Charging',
        subtitle:
          'Power meets personality. The custom-designed ring charger, in the iconic Diesel Red with the signature graphic logo, powers the ring for up to 6 days on a single charge.',
        image: '/web_v2/uh-x-diesel/specs/final/phantom-charge.png',
      },
    ],
    galleryImages: [
      '/web_v2/uh-x-diesel/gallery/phantom/1.png',
      '/web_v2/uh-x-diesel/gallery/phantom/2.png',
      '/web_v2/uh-x-diesel/gallery/phantom/3.png',
      '/web_v2/uh-x-diesel/gallery/phantom/4.png',
      '/web_v2/uh-x-diesel/gallery/phantom/5.png',
    ],
  },
  Cryo: {
    leftImagePath: {
      web: '/web_v2/uh-x-diesel/variants/cryo/cryo-lifestyle-gradient.png',
      mobile: '/web_v2/uh-x-diesel/variants/cryo/cryo-lifestyle-v1.png',
    },
    rightImagePath: {
      web: '/web_v2/uh-x-diesel/variants/cryo/cryo-ring-gradient.png',
      mobile: '/web_v2/uh-x-diesel/variants/cryo/cryo-render-v1.png',
    },
    slantedText:
      'Bringing Diesel’s iconic    Double-D design to Ultrahuman’s    advanced     technology,     the shiny Cryo variant reflects        the integration       of protocols in the bleeding edge of health and fashion.  Icy in tone, effortless                in presence.',
    modelImagePath: '/web_v2/uh-x-diesel/specs/cryo-model.png',
    accordionItems: [
      {
        title: 'Durability',
        subtitle:
          'Built to last with an outer shell made from Space-Grade Titanium. With up to 100 m/328 ft water resistance, you can explore the uncharted, uninterrupted.',
        image: '/web_v2/uh-x-diesel/specs/final/cryo-durability.png',
      },
      {
        title: 'Tech & Build',
        subtitle:
          'Designed for comfort with a seamless, hypoallergenic resin. The inner shell houses a 6-Axis Motion Sensor for movement, a PPG Sensor for Heart Rate & Blood Oxygen, and a medical-grade temperature sensor.',
        image: '/web_v2/uh-x-diesel/specs/final/cryo-tech.png',
      },
      {
        title: 'Battery & Charging',
        subtitle:
          'Power meets personality. The custom-designed ring charger, in the iconic Diesel Red with the signature graphic logo, powers the ring for up to 6 days on a single charge.',
        image: '/web_v2/uh-x-diesel/specs/final/cryo-charge.png',
      },
    ],
    galleryImages: [
      '/web_v2/uh-x-diesel/gallery/cryo/1.png',
      '/web_v2/uh-x-diesel/gallery/cryo/2.png',
      '/web_v2/uh-x-diesel/gallery/cryo/3.png',
      '/web_v2/uh-x-diesel/gallery/cryo/4.png',
    ],
  },
};

interface VariantSelectionContextType {
  selectedVariant: Variant;
  setSelectedVariant: (variant: Variant) => void;
  generateBuyLink: (variant: Variant) => string;
  handleBuyClick: () => void;
  variantData: VariantData;
}

const initialContext: VariantSelectionContextType = {
  selectedVariant: 'Cryo',
  setSelectedVariant: () => null,
  generateBuyLink: () => '',
  handleBuyClick: () => null,
  variantData: VARIANT_DATA.Cryo,
};

export const VariantSelectionContext =
  createContext<VariantSelectionContextType>(initialContext);

export const useVariantSelection = () => {
  const context = useContext(VariantSelectionContext);
  if (!context) {
    throw new Error(
      'useVariantSelection must be used within VariantSelectionContextProvider',
    );
  }
  return context;
};

interface VariantSelectionContextProviderProps {
  children: ReactNode;
  defaultVariant?: Variant;
}

const BUY_URL_MAP: Record<Variant, string> = {
  Phantom: '/diesel-ultrahuman-ring/buy/?color=phantom',
  Cryo: '/diesel-ultrahuman-ring/buy/?color=cryo',
};

export const VariantSelectionContextProvider: React.FC<
  VariantSelectionContextProviderProps
> = ({ children, defaultVariant = 'Cryo' }) => {
  const [selectedVariant, setSelectedVariant] =
    useState<Variant>(defaultVariant);
  const router = useRouter();
  const formatLink = useFormatLink();

  const generateBuyLink = useCallback((variant: Variant): string => {
    return BUY_URL_MAP[variant];
  }, []);

  const handleBuyClick = useCallback(() => {
    const buyLink = BUY_URL_MAP[selectedVariant];
    const formattedLink = formatLink(buyLink);
    router.push(formattedLink as string);
  }, [selectedVariant, formatLink, router]);

  const variantData = useMemo(() => {
    return VARIANT_DATA[selectedVariant];
  }, [selectedVariant]);

  const value = useMemo<VariantSelectionContextType>(
    () => ({
      selectedVariant,
      setSelectedVariant,
      generateBuyLink,
      handleBuyClick,
      variantData,
    }),
    [
      selectedVariant,
      setSelectedVariant,
      generateBuyLink,
      handleBuyClick,
      variantData,
    ],
  );

  return (
    <VariantSelectionContext.Provider value={value}>
      {children}
    </VariantSelectionContext.Provider>
  );
};
